import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/context/LangContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = ["Tout", "Car Show", "Beach Party", "Foam Party", "Soirée Corporative", "Gala", "Autres"];

export default function Gallery() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [activeCategory, setActiveCategory] = useState("Tout");
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    axios.get(`${API}/content/gallery`).then(r => setItems(r.data)).catch(() => {});
  }, []);

  const filtered = activeCategory === "Tout"
    ? items
    : items.filter(item => item.category === activeCategory);

  const close = useCallback(() => setSelectedIndex(null), []);
  const next = useCallback(() => setSelectedIndex(i => (i === null ? null : (i + 1) % filtered.length)), [filtered.length]);
  const prev = useCallback(() => setSelectedIndex(i => (i === null ? null : (i - 1 + filtered.length) % filtered.length)), [filtered.length]);

  useEffect(() => {
    if (selectedIndex === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      else if (e.key === "ArrowRight") next();
      else if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedIndex, close, next, prev]);

  if (!items.length) return null;

  const selected = selectedIndex !== null ? filtered[selectedIndex] : null;

  return (
    <section className="relative py-16 md:py-32 bg-[#050505]" data-testid="gallery-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
        <div className="mb-10 md:mb-16 text-center">
          <span className="text-[#E60000] text-xs sm:text-sm uppercase tracking-[0.3em] font-medium font-body">{t.gallery.section_tag}</span>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-semibold uppercase text-white mt-3" data-testid="gallery-title">
            {t.gallery.title}{" "}<span className="text-[#E60000]">{t.gallery.title_accent}</span>
          </h2>
        </div>

        {/* Category filters */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-8 sm:mb-12" data-testid="gallery-filters">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`text-xs sm:text-sm font-bold uppercase tracking-[0.15em] px-4 sm:px-6 py-2 sm:py-2.5 transition-all duration-300 ${
                activeCategory === cat
                  ? "bg-[#E60000] text-white"
                  : "border border-white/15 text-[#A3A3A3] hover:border-[#E60000] hover:text-white"
              }`}
              data-testid={`gallery-filter-${cat.replace(/\s/g, "-").toLowerCase()}`}
            >
              {cat}
            </button>
          ))}
        </div>

        {/* Gallery grid with spotlight hover */}
        <motion.div
          layout
          className="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3"
          onMouseLeave={() => setHovered(null)}
        >
          <AnimatePresence>
            {filtered.map((item, i) => {
              const dimmed = hovered !== null && hovered !== i;
              return (
                <motion.div
                  key={item.id || i}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.4 }}
                  onMouseEnter={() => setHovered(i)}
                  onClick={() => setSelectedIndex(i)}
                  className={`group relative aspect-square overflow-hidden cursor-pointer border transition-all duration-500 ${
                    hovered === i ? "border-[#E60000] z-10" : "border-white/5"
                  }`}
                  style={{
                    opacity: dimmed ? 0.35 : 1,
                    filter: dimmed ? "grayscale(0.7) brightness(0.6)" : "none",
                    transform: hovered === i ? "scale(1.03)" : "scale(1)",
                    boxShadow: hovered === i ? "0 20px 60px -10px rgba(230,0,0,0.5)" : "none",
                  }}
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  {/* Spotlight sheen */}
                  <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(circle at 50% 30%, rgba(230,0,0,0.25), transparent 60%)" }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex items-end">
                    <div className="p-3 sm:p-5 translate-y-4 group-hover:translate-y-0 transition-transform duration-400">
                      <h3 className="font-heading text-sm sm:text-lg font-semibold uppercase text-white tracking-tight leading-tight">{item.title}</h3>
                      <p className="font-body text-[10px] sm:text-xs text-white/60 mt-0.5">{item.description}</p>
                      {item.category && (
                        <span className="inline-block mt-2 text-[8px] sm:text-[9px] uppercase tracking-wider text-[#E60000] border border-[#E60000]/40 px-1.5 py-0.5 bg-black/50">
                          {item.category}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>

        {filtered.length === 0 && (
          <p className="text-center text-[#A3A3A3] font-body text-sm mt-8">Aucune photo dans cette catégorie.</p>
        )}
      </div>

      {/* Lightbox with keyboard + arrow navigation */}
      <AnimatePresence>
        {selected && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4 sm:p-6" onClick={close} data-testid="gallery-lightbox">
            <button onClick={close} className="absolute top-4 right-4 sm:top-6 sm:right-6 w-11 h-11 flex items-center justify-center border border-white/20 text-white hover:border-[#E60000] hover:bg-[#E60000]/20 transition-colors z-20" data-testid="lightbox-close"><X size={20} /></button>

            {filtered.length > 1 && (
              <>
                <button onClick={(e) => { e.stopPropagation(); prev(); }} className="absolute left-3 sm:left-8 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center border border-white/20 text-white hover:border-[#E60000] hover:bg-[#E60000]/20 transition-colors z-20" data-testid="lightbox-prev"><ChevronLeft size={22} /></button>
                <button onClick={(e) => { e.stopPropagation(); next(); }} className="absolute right-3 sm:right-8 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 flex items-center justify-center border border-white/20 text-white hover:border-[#E60000] hover:bg-[#E60000]/20 transition-colors z-20" data-testid="lightbox-next"><ChevronRight size={22} /></button>
              </>
            )}

            <motion.div key={selectedIndex} initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} transition={{ duration: 0.3 }} className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
              <img src={selected.image} alt={selected.title} className="w-full h-auto max-h-[78vh] object-contain" data-testid="lightbox-image" />
              <div className="mt-4 flex items-center justify-between gap-4">
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-semibold uppercase text-white tracking-tight">{selected.title}</h3>
                  <p className="font-body text-sm text-[#A3A3A3]">{selected.description}</p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {selected.category && (
                    <span className="text-xs uppercase tracking-wider text-[#E60000] border border-[#E60000]/30 px-3 py-1">{selected.category}</span>
                  )}
                  <span className="font-body text-xs text-white/40 tabular-nums">{selectedIndex + 1} / {filtered.length}</span>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
